import React, { useCallback, useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography, makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Badge, Button, AsyncSection } from "../ds";
import tokens from "../designTokens";
import { reviewQueue, reviewAgreement, qualityEpisode, labelReview } from "./api";
import { EpisodeTrace } from "./EpisodesPanel";
import { fmtTime } from "./shared";

const GOALS = ["achieved", "partial", "failed", "not_decidable"];
const HANDOFFS = ["correct", "unnecessary", "missed", "na"];
const QUEUE_STATES = ["open", "labelled", "dismissed"];
const CLAIM_VERDICTS = ["supported", "contradicted", "unsupported", "not_a_claim"];

const useStyles = makeStyles({
  wrap: { padding: 16, display: "flex", flexDirection: "column", gap: 12 },
  item: { border: `1px solid ${tokens.border}`, borderRadius: 8, padding: "10px 12px", background: tokens.surface },
  meta: { fontFamily: tokens.fontBody, fontSize: 11.5, color: tokens.textHint },
  form: { display: "flex", flexDirection: "column", gap: 10, marginTop: 10 },
  kappa: { display: "flex", gap: 16, fontFamily: tokens.fontBody, fontSize: 12.5, color: tokens.textPrimary, flexWrap: "wrap" },
});

const kappaText = (s) => (s && s.kappa != null ? `${s.kappa.toFixed(2)} (n=${s.n})` : s?.n ? `n/a (n=${s.n})` : "no labels yet");

function AgreementPanel({ refreshKey }) {
  const classes = useStyles();
  const [state, setState] = useState({ loading: true, notFound: false, error: null, data: null });
  useEffect(() => {
    let alive = true;
    reviewAgreement({}).then((r) => alive && setState({ ...r, loading: false }));
    return () => {
      alive = false;
    };
  }, [refreshKey]);
  return (
    <AsyncSection loading={state.loading} notFound={state.notFound} error={state.error}>
      <div className={classes.kappa} data-testid="kappa-panel">
        <span>Judge vs human, Cohen's κ</span>
        <span>Goal: <b>{kappaText(state.data?.goal)}</b></span>
        <span>Critical claims: <b>{kappaText(state.data?.claims)}</b></span>
      </div>
      {state.data?.sample === "judge-flagged" && (
        <div className={classes.meta} data-testid="kappa-caveat">
          Computed on judge-flagged episodes only (the review queue), so it is not a fleet-wide agreement estimate.
        </div>
      )}
    </AsyncSection>
  );
}

function LabelForm({ item, onDone }) {
  const classes = useStyles();
  const [detail, setDetail] = useState({ loading: true, notFound: false, error: null, data: null });
  const [goal, setGoal] = useState("");
  const [handoff, setHandoff] = useState("");
  const [claims, setClaims] = useState({});
  const [notes, setNotes] = useState("");
  const [promote, setPromote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    qualityEpisode(item.verdict_id).then((r) => alive && setDetail({ ...r, loading: false }));
    return () => {
      alive = false;
    };
  }, [item.verdict_id]);

  const verdictClaims = detail.data?.verdict?.critical_claims || [];

  const submit = async () => {
    setSaving(true);
    setError(null);
    const body = {
      goal: goal || null,
      handoff: handoff || null,
      claims: Object.entries(claims).filter(([, v]) => v && v !== "not_a_claim").slice(0, 50).map(([claim_ref, verdict]) => ({ claim_ref, verdict })),
      notes,
      promote_golden: promote,
    };
    const r = await labelReview(item.verdict_id, body);
    setSaving(false);
    if (r.error) setError(r.error);
    else onDone();
  };

  return (
    <div className={classes.form}>
      <EpisodeTrace episodeId={item.verdict_id} />
      <Box display="flex" gridGap={12}>
        <TextField select size="small" variant="outlined" label="Goal" value={goal} onChange={(e) => setGoal(e.target.value)} style={{ minWidth: 160 }}>
          {GOALS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>
        <TextField select size="small" variant="outlined" label="Handoff" value={handoff} onChange={(e) => setHandoff(e.target.value)} style={{ minWidth: 160 }}>
          {HANDOFFS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>
      </Box>
      {verdictClaims.map((c) => (
        <Box key={c.claim_ref} display="flex" alignItems="center" gridGap={10}>
          <Typography component="span" className={classes.meta} style={{ minWidth: 90 }}>{c.claim_ref}</Typography>
          <Badge tone="neutral" subtle>judge: {c.verdict}</Badge>
          <TextField select size="small" variant="outlined" label="Your verdict" value={claims[c.claim_ref] || ""} onChange={(e) => setClaims((s) => ({ ...s, [c.claim_ref]: e.target.value }))} style={{ minWidth: 170 }}>
            {CLAIM_VERDICTS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
        </Box>
      ))}
      <TextField multiline minRows={2} size="small" variant="outlined" label="Notes" value={notes} inputProps={{ maxLength: 2000 }} helperText={`${notes.length}/2000`} onChange={(e) => setNotes(e.target.value)} />
      <FormControlLabel control={<Checkbox checked={promote} onChange={(e) => setPromote(e.target.checked)} />} label="Promote to golden set" />
      {error && <Alert severity="error">{error}</Alert>}
      <Box>
        <Button size="sm" disabled={saving || (!goal && !handoff)} onClick={submit}>{saving ? "Saving…" : "Save label"}</Button>
      </Box>
    </div>
  );
}

export default function ReviewQueue({ refreshKey }) {
  const classes = useStyles();
  const [state, setState] = useState({ loading: true, notFound: false, error: null, data: null });
  const [open, setOpen] = useState(null);
  const [agreementKey, setAgreementKey] = useState(0);
  const [queueState, setQueueState] = useState("open");

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    return reviewQueue({ state: queueState, limit: 50 }).then((r) => setState({ ...r, loading: false }));
  }, [queueState]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const items = state.data?.items || [];

  return (
    <div className={classes.wrap}>
      <AgreementPanel refreshKey={`${refreshKey}-${agreementKey}`} />
      <TextField select size="small" variant="outlined" label="State" value={queueState} onChange={(e) => { setOpen(null); setQueueState(e.target.value); }} style={{ width: 160 }}>
        {QUEUE_STATES.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
      </TextField>
      <AsyncSection loading={state.loading} notFound={state.notFound} error={state.error} empty={!items.length} emptyTitle={`No ${queueState} items`}>
        <div>
          {items.map((it) => (
            <div key={it.verdict_id} className={classes.item} style={{ marginBottom: 8 }}>
              <Box display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
                <Typography component="span" className={classes.meta}>{it.project_id}{it.agent_id ? ` · ${it.agent_id}` : ""} · {fmtTime(it.created_at)}</Typography>
                {(it.reasons || []).map((r) => <Badge key={r} tone="warning" subtle>{r}</Badge>)}
                {it.dismissed_reason && <Badge tone="neutral" subtle>{it.dismissed_reason === "cleared_on_rejudge" ? "cleared on re-judge" : it.dismissed_reason}</Badge>}
                <span style={{ marginLeft: "auto" }}>
                  <Button size="sm" variant="text" onClick={() => setOpen(open === it.verdict_id ? null : it.verdict_id)}>
                    {open === it.verdict_id ? "Close" : "Review"}
                  </Button>
                </span>
              </Box>
              {open === it.verdict_id && it.state !== "open" && <EpisodeTrace episodeId={it.verdict_id} />}
              {open === it.verdict_id && it.state === "open" && (
                <LabelForm
                  item={it}
                  onDone={() => {
                    setOpen(null);
                    setAgreementKey((k) => k + 1);
                    load();
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </AsyncSection>
    </div>
  );
}
